// Profil gamifikasi milik nama yang sedang login: disimpan di perangkat, disinkronkan ke server
// (/api/profil, digabung per hari), dicatat dari peristiwa kuis & belajar, lalu dibandingkan
// dengan yang sudah "dilihat" anak untuk memunculkan perayaan (misi, lencana, level, peti).
import { store } from '../core/storage.js';
import { S } from '../app/state.js';
import { MODE_DEMO, daftarRiwayat, ambilRiwayat } from '../app/riwayat.js';
import { slugNama } from '../../lib/riwayat.js';
import { bersihkanProfil, gabungProfil, tanggalLokal } from '../../lib/profil.js';
import { TOPIK_PER_MAPEL } from '../subjects/topik.js';
import { lupakanPeringkat } from './peringkat.js';
import { statusMisi, statusPekan, hitungStreak, totalXP, infoLevel, statistik, statusLencana, kostumTerbuka, pilihStiker, KOSTUM } from './aturan.js';

// Per akun (lib/akun.js); tanpa akun (versi demo) per nama.
export const slug = () => S.akun || slugNama(S.nama);
const kunci = () => 'tka-game-' + slug();
const kunciMisi = t => `tka-game-misi-${slug()}-${t}`;
export const mapelAktif = () => (S.konten?.config?.mapel || []).filter(m => m.aktif).map(m => m.id);

let P = null, kombo = 0, tSinkron = null, sedangIsiAwal = false;
const antre = [];
const pendengar = new Set();

export function profil() {
  if (!P || P._nama !== kunci()) {
    // Data perangkat dari sebelum ada akun (disimpan per nama) dipindahkan sekali ke kunci akun.
    const lama = 'tka-game-' + slugNama(S.nama);
    if (lama !== kunci() && !store.get(kunci(), null) && store.get(lama, null)) { store.set(kunci(), store.get(lama, null)); store.del(lama); }
    P = bersihkanProfil(store.get(kunci(), null)); P._nama = kunci(); pindahkanProgresLama();
  }
  return P;
}

// Progres modul belajar dulu disimpan terpisah per perangkat (tka-belajar-<nama>). Pindahkan sekali
// ke profil supaya ikut disinkronkan; kunci lama dihapus setelah tersimpan.
function pindahkanProgresLama() {
  const lama = 'tka-belajar-' + String(S.nama || 'anonim').toLowerCase().replace(/[^a-z0-9]+/g, '-');
  const isi = store.get(lama, null);
  if (!isi || typeof isi !== 'object') return;
  const dari = { belajar: Object.fromEntries(Object.entries(isi).map(([id, m]) => [id, { t: m?.terakhir || 0, s: !!m?.selesai, b: m?.bintang, w: m?.waktu || 0 }])) };
  const nama = P._nama;
  P = gabungProfil(P, dari); P._nama = nama;
  const { _nama, ...simpanan } = P;
  store.set(kunci(), simpanan);
  store.del(lama);
}
function simpan() {
  const p = profil(), { _nama, ...isi } = p;
  store.set(kunci(), isi);
  pendengar.forEach(f => f());
  jadwalSinkron();
}
/** Daftarkan fungsi yang dipanggil setiap profil berubah (mis. kartu beranda). */
export const saatBerubah = f => { pendengar.add(f); return () => pendengar.delete(f); };

const hari = (t = tanggalLokal()) => (profil().hari[t] ||= {});

/* ---------------- ringkasan untuk tampilan ---------------- */

export function ringkas(t = tanggalLokal()) {
  const p = profil(), s = slug(), ma = mapelAktif();
  const xp = totalXP(p, s, ma), level = infoLevel(xp), stat = statistik(p, s, ma, t), lencana = statusLencana(stat, ma);
  return { p, xp, level, streak: hitungStreak(p, t), misi: statusMisi(p, t, s, ma), pekan: statusPekan(p, t), stat, lencana, kostum: kostumTerbuka(level.level, lencana) };
}

/* ---------------- pencatatan peristiwa ---------------- */

/** Kunci topik soal untuk peta kemampuan (generator atau bank soal). */
export function topikSoal(q, mapel) {
  const daftar = TOPIK_PER_MAPEL[mapel] || {}, k = q.gen || String(q.topic || '').replace(/^bank:/, '');
  return daftar[k] ? k : null;
}

let xpAwal = 0;
/** Dipanggil saat ronde dimulai: kombo diulang dan XP awal dicatat untuk layar hasil. */
export function mulaiRonde() { kombo = 0; xpAwal = S.nama ? ringkas().xp : 0; }
export const xpAwalRonde = () => xpAwal;

export function catatJawaban(q, ok, mapel = S.mapel) {
  if (!S.nama) return;
  const h = hari();
  h.s = (h.s || 0) + 1;
  if (ok) {
    h.b = (h.b || 0) + 1;
    if (mapel === 'matematika') h.bm = (h.bm || 0) + 1;
    if (mapel === 'bahasa-indonesia') h.bi = (h.bi || 0) + 1;
  }
  kombo = ok ? kombo + 1 : 0;
  h.k = Math.max(h.k || 0, kombo);
  const k = topikSoal(q, mapel);
  if (k) {
    const t = ((profil().topik[mapel] ||= {})[k] ||= { n: 0, b: 0, h: '' });
    t.n++; if (ok) t.b++; t.h = (t.h + (ok ? '1' : '0')).slice(-10);
  }
  simpan();
}

export function catatRonde({ mapel, nilai, saran = false }) {
  if (!S.nama) return;
  const h = hari();
  h.r = (h.r || 0) + 1;
  h.n = [...(h.n || []), Math.round(nilai)].slice(-50);
  h.m = [...new Set([...(h.m || []), mapel])];
  if (saran) h.sa = (h.sa || 0) + 1;
  simpan();
}

export function catatPembahasan() { if (!S.nama) return; const h = hari(); h.p = (h.p || 0) + 1; simpan(); }
export function catatLangkah() { if (!S.nama) return; const h = hari(); h.l = (h.l || 0) + 1; simpan(); }
export function catatModul(id, bintang) {
  if (!S.nama) return;
  const h = hari();
  h.mo = { ...(h.mo || {}), [id]: Math.max(h.mo?.[id] || 0, bintang) };
  simpan();
}

/** Buka peti harian (semua misi selesai). Mengembalikan stiker yang didapat (atau null bila koleksi lengkap). */
export function bukaPeti() {
  const r = ringkas();
  if (!r.misi.semua || r.misi.petiDibuka) return undefined;
  const h = hari(), st = pilihStiker(profil());
  h.kt = true;
  if (st) h.st = [...(h.st || []), st.id];
  simpan();
  return st;
}
/** Buka peti pekan (target pekan tercapai). */
export function bukaPetiPekan() {
  const r = ringkas();
  if (!r.pekan.tercapai || r.pekan.diklaim) return undefined;
  const h = hari(), st = pilihStiker(profil());
  h.kw = r.pekan.kunci;
  if (st) h.st = [...(h.st || []), st.id];
  simpan();
  return st;
}

/* ---------------- progres modul belajar ---------------- */

/** Progres satu modul dalam bentuk yang dipakai mesin belajar, atau null. */
export function progresBelajar(id) {
  const m = profil().belajar[id];
  return m ? { terakhir: m.t || 0, selesai: !!m.s, bintang: m.b || 0, waktu: m.w || 0 } : null;
}
export function simpanProgresBelajar(id, { terakhir, selesai, bintang } = {}) {
  if (!S.nama) return;
  const b = profil().belajar, m = { ...(b[id] || { t: 0 }) };
  if (terakhir !== undefined) m.t = Math.max(0, Math.min(60, Math.round(terakhir)));
  if (selesai) m.s = true;
  if ([1, 2, 3].includes(bintang)) m.b = Math.max(m.b || 0, bintang);
  m.w = Date.now();
  b[id] = m;
  simpan();
}

export function pakaiKostum(id) {
  if (!KOSTUM.some(k => k.id === id)) return;
  const p = profil();
  p.kostum = id; p.kostumWaktu = Date.now();
  simpan();
}

/* ---------------- perayaan: bandingkan dengan yang sudah dilihat ---------------- */

/**
 * Cari pencapaian baru sejak terakhir dilihat, masukkan ke antrean perayaan, lalu tandai sudah dilihat.
 * @returns {object[]} peristiwa baru: { jenis: 'misi'|'peti'|'pekan'|'lencana'|'level', ... }
 */
export function periksaBaru() {
  if (!S.nama || sedangIsiAwal) return [];
  const r = ringkas(), p = r.p, d = p.dilihat, t = tanggalLokal(), baru = [];
  const misiDilihat = new Set(store.get(kunciMisi(t), []));
  for (const m of r.misi.misi) if (m.selesai && !misiDilihat.has(m.id)) { baru.push({ jenis: 'misi', misi: m }); misiDilihat.add(m.id); }
  store.set(kunciMisi(t), [...misiDilihat]);
  if (baru.length && r.misi.semua && !r.misi.petiDibuka) baru.push({ jenis: 'peti' });
  const lv = d.level || 1;
  if (r.level.level > lv) baru.push({ jenis: 'level', level: r.level, kostumBaru: r.kostum.filter(k => k.level > lv && k.level <= r.level.level) });
  const dl = d.lencana || {};
  for (const l of r.lencana) if (l.tingkat > (dl[l.id] || 0)) baru.push({ jenis: 'lencana', lencana: l, tingkat: l.tingkat, kostumBaru: r.kostum.filter(k => k.lencana?.[0] === l.id && k.lencana[1] > (dl[l.id] || 0) && k.lencana[1] <= l.tingkat) });
  if (r.pekan.tercapai && !r.pekan.diklaim && !store.get(kunciMisi('pekan-' + r.pekan.kunci), false)) { baru.push({ jenis: 'pekan' }); store.set(kunciMisi('pekan-' + r.pekan.kunci), true); }
  p.dilihat = { ...d, level: r.level.level, lencana: Object.fromEntries(r.lencana.filter(l => l.tingkat).map(l => [l.id, l.tingkat])) };
  if (baru.length) { simpan(); antre.push(...baru); }
  return baru;
}
export const ambilAntrean = () => antre.splice(0);

/* ---------------- sinkron ke server ---------------- */

function jadwalSinkron() {
  if (MODE_DEMO) return;
  clearTimeout(tSinkron);
  tSinkron = setTimeout(sinkron, 4000);
}

/** Kirim profil ke server; server menggabungkan dengan salinannya dan mengembalikan hasil gabungan. */
export async function sinkron() {
  if (MODE_DEMO || !S.nama || !navigator.onLine) return false;
  clearTimeout(tSinkron);
  const nama = kunci(), { _nama, ...isi } = profil();
  try {
    const res = await fetch('/api/profil', { method: 'PUT', credentials: 'same-origin', headers: { 'content-type': 'application/json' }, body: JSON.stringify(isi) });
    if (!res.ok || nama !== kunci()) return false;
    const d = await res.json();
    P = gabungProfil(profil(), d.profil); P._nama = nama;
    const { _nama: _, ...simpanan } = P;
    store.set(kunci(), simpanan);
    lupakanPeringkat(); // XP di server sudah diperbarui
    pendengar.forEach(f => f());
    return true;
  } catch (e) { return false; }
}

/**
 * Isi awal dari riwayat lama (sekali per nama): hasil kuis sebelum fitur ini ada ikut dihitung
 * ke peta kemampuan, streak, dan lencana. Pencapaian dari riwayat lama tidak dirayakan satu per satu.
 * @returns {Promise<number>} banyak ronde yang ditambahkan
 */
export async function isiAwal({ maks = 30 } = {}) {
  const p = profil();
  if (p.dilihat.isiAwal || !S.nama) return 0;
  let n = 0;
  sedangIsiAwal = true;
  try {
    const { riwayat, offline } = await daftarRiwayat();
    if (offline) { sedangIsiAwal = false; return 0; }
    const nama = S.nama;
    for (const r of riwayat.filter(x => !x.tertunda).slice(0, maks)) {
      const rek = await ambilRiwayat(r.id).catch(() => null);
      if (!rek?.soal || nama !== S.nama) continue;
      const t = tanggalLokal(new Date(rek.selesai || Date.now())), h = (profil().hari[t] ||= {});
      for (const s of rek.soal) {
        if (s.skip) continue;
        h.s = (h.s || 0) + 1;
        if (s.ok) { h.b = (h.b || 0) + 1; if (rek.mapel === 'matematika') h.bm = (h.bm || 0) + 1; if (rek.mapel === 'bahasa-indonesia') h.bi = (h.bi || 0) + 1; }
        const k = topikSoal(s.q || {}, rek.mapel);
        if (k) { const tp = ((profil().topik[rek.mapel] ||= {})[k] ||= { n: 0, b: 0, h: '' }); tp.n++; if (s.ok) tp.b++; tp.h = (tp.h + (s.ok ? '1' : '0')).slice(-10); }
      }
      h.r = (h.r || 0) + 1; h.n = [...(h.n || []), Math.round(rek.nilai)].slice(-50); h.m = [...new Set([...(h.m || []), rek.mapel])];
      n++;
    }
  } catch (e) { sedangIsiAwal = false; return 0; }
  sedangIsiAwal = false;
  const r = ringkas(), q = profil();
  // Pencapaian dari riwayat lama langsung ditandai sudah dilihat; misi hari ini tetap dirayakan nanti.
  q.dilihat = { ...q.dilihat, isiAwal: true, level: r.level.level, lencana: Object.fromEntries(r.lencana.filter(l => l.tingkat).map(l => [l.id, l.tingkat])) };
  simpan();
  return n;
}
